import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CustomerSelectionProvider } from './context/CustomerSelectionContext';
import { ThemeProvider } from './context/ThemeContext';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Reports from './pages/Reports';
import { SettingsView } from './pages/SettingsView';
import UserSettingsView from './pages/UserSettingsView';
import PartnerMarketplaceView from './pages/PartnerMarketplaceView';
import NinjaRmmView from './pages/integrations/NinjaRmmView';
import { initialiseMsal } from './auth/msal';
import { config } from './config/environment';

// New placeholder pages
import TasksView from './pages/TasksView';
import ContractsView from './pages/ContractsView';
import CalendarView from './pages/CalendarView';
import ApprovalsView from './pages/ApprovalsView';
import { RequirePartnerFeature } from './components/RequirePartnerFeature';
import ProposalsView from './pages/proposals/ProposalsView';
import ProposalDetailView from './pages/proposals/ProposalDetailView';
import ProposalCreateView from './pages/proposals/ProposalCreateView';
import LeadsView from './pages/leads/LeadsView';
import LeadDetailView from './pages/leads/LeadDetailView';
import LeadCreateView from './pages/leads/LeadCreateView';
import ProjectsView from './pages/projects/ProjectsView';
import ProjectDetailView from './pages/projects/ProjectDetailView';
import ProjectCreateView from './pages/projects/ProjectCreateView';

// Auth guard component to handle routing based on auth state
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, hasPartnerAccess, signInWithAnotherAccount } = useAuth();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin mx-auto" />
          </div>
          <p className="text-sm text-slate-500 font-medium mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show partner access warning if authenticated but no partner access
  if (!hasPartnerAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 shadow-lg">
              A
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Partner Access Required</h1>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              You are not registered as a partner in our system. Please contact your administrator to set up partner access.
            </p>
          </div>
          <p className="text-center text-sm text-slate-500 mb-6">
            Use your partner organization account to sign in
          </p>
          <button
            type="button"
            onClick={() => void signInWithAnotherAccount()}
            className="w-full rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-orange-700"
          >
            Sign in with another account
          </button>
        </div>
      </div>
    );
  }

  // Render protected content
  return <>{children}</>;
}

// Main app routes wrapped with AppLayout
function AppRoutes() {
  return (
    <AuthGuard>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<AppLayout><Navigate to="/dashboard" replace /></AppLayout>} />

        {/* Existing functional routes */}
        <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
        <Route path="/customers" element={<AppLayout><Customers /></AppLayout>} />
        <Route path="/customers/:customerId" element={<AppLayout><CustomerDetail /></AppLayout>} />
        <Route path="/marketplace" element={<AppLayout><PartnerMarketplaceView /></AppLayout>} />
        <Route path="/integrations/ninjaone" element={<AppLayout><NinjaRmmView /></AppLayout>} />
        <Route path="/reports" element={<AppLayout><Reports /></AppLayout>} />
        <Route path="/settings" element={<AppLayout><SettingsView /></AppLayout>} />
        <Route path="/user-settings" element={<AppLayout><UserSettingsView /></AppLayout>} />

        {/* Applications - new placeholder pages */}
        <Route path="/tasks" element={<AppLayout><TasksView /></AppLayout>} />
        <Route path="/contracts" element={<AppLayout><RequirePartnerFeature feature="contracts"><ContractsView /></RequirePartnerFeature></AppLayout>} />
        <Route path="/calendar" element={<AppLayout><CalendarView /></AppLayout>} />
        <Route path="/approvals" element={<AppLayout><RequirePartnerFeature feature="recurringTasks"><ApprovalsView /></RequirePartnerFeature></AppLayout>} />

        {/* Proposals - new placeholder pages */}
        <Route path="/proposals" element={<AppLayout><ProposalsView /></AppLayout>} />
        <Route path="/proposals/create" element={<AppLayout><ProposalCreateView /></AppLayout>} />
        <Route path="/proposals/templates" element={<AppLayout><ProposalsView /></AppLayout>} />
        <Route path="/proposals/:proposalId" element={<AppLayout><ProposalDetailView /></AppLayout>} />

        {/* Leads - new placeholder pages */}
        <Route path="/leads" element={<AppLayout><LeadsView /></AppLayout>} />
        <Route path="/leads/create" element={<AppLayout><LeadCreateView /></AppLayout>} />
        <Route path="/leads/pipeline" element={<AppLayout><LeadsView /></AppLayout>} />
        <Route path="/leads/:leadId" element={<AppLayout><LeadDetailView /></AppLayout>} />

        {/* Projects - new placeholder pages */}
        <Route path="/projects" element={<AppLayout><ProjectsView /></AppLayout>} />
        <Route path="/projects/create" element={<AppLayout><ProjectCreateView /></AppLayout>} />
        <Route path="/projects/timeline" element={<AppLayout><ProjectsView /></AppLayout>} />
        <Route path="/projects/:projectId" element={<AppLayout><ProjectDetailView /></AppLayout>} />

        {/* Legacy routes for backwards compatibility */}
        <Route path="/integrations" element={<Navigate to="/settings?tab=integrations" replace />} />
        <Route path="/partner-marketplace" element={<Navigate to="/marketplace" replace />} />
        <Route path="/customer-overview" element={<Navigate to="/customers" replace />} />
        <Route path="/tenants" element={<Navigate to="/customers" replace />} />
      </Routes>
    </AuthGuard>
  );
}

function App() {
  const [msalReady, setMsalReady] = useState(false);

  useEffect(() => {
    const initMsal = async () => {
      // DEV MODE: Skip MSAL initialization
      if (config.features.devMode) {
        setMsalReady(true);
        return;
      }
      
      try {
        // initialiseMsal handles initialize(), handleRedirectPromise(),
        // active-account wiring, and a boot-time probe that forces a fresh
        // loginRedirect if the cached account can't silently obtain a Core
        // API token. If it redirects, the page navigates away and this never
        // resolves.
        await initialiseMsal();
        setMsalReady(true);
      } catch (error) {
        setMsalReady(true); // Still render the app even if MSAL fails
      }
    };

    initMsal();
  }, []);

  if (!msalReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin mx-auto" />
          </div>
          <p className="text-sm text-slate-500 font-medium mt-4">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <CustomerSelectionProvider>
          <Router>
            <AppRoutes />
          </Router>
        </CustomerSelectionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
